import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  NetworkCheck as NetworkCheckIcon,
  Bolt as BoltIcon,
  SwapHoriz as SwapHorizIcon,
  PlaylistAdd as PlaylistAddIcon,
  LinkOff as LinkOffIcon,
  Autorenew as AutorenewIcon,
  SignalCellularAlt as SignalIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { designTokens as t } from '../theme';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const generateSafeId = (proxyUrl) => {
  let hash = 0;
  for (let i = 0; i < proxyUrl.length; i++) {
    const char = proxyUrl.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return `proxy-${Math.abs(hash).toString(36)}`;
};

const extractUsedBy = (status) => {
  if (!status || typeof status !== 'string') return '';
  if (status.includes('Utilisé par la session:')) {
    return status.replace('Utilisé par la session:', '').trim();
  }
  return '';
};

// Petite carte de statistique
function StatCard({ icon, label, value, color }) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}1f`,
            color,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.78rem', color: t.muted, fontWeight: 500 }}>{label}</Typography>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function FixedProxies() {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState([]);

  // dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);

  const [newProxy, setNewProxy] = useState('');
  const [newRotate, setNewRotate] = useState('');
  const [bulkText, setBulkText] = useState('');

  // édition du lien de rotation 4G d'un proxy existant
  const [rotateEdit, setRotateEdit] = useState({ open: false, proxy: '', url: '' });

  // test d'un lien de rotation (résultat brut de la requête)
  const [rotateTesting, setRotateTesting] = useState(false);
  const [rotateTestResult, setRotateTestResult] = useState(null);

  // réassignation
  const [sessions, setSessions] = useState([]);
  const [reassignSel, setReassignSel] = useState([]);
  const [reassignTarget, setReassignTarget] = useState('direct');
  const [customProxy, setCustomProxy] = useState('');

  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({ total: 0, available: 0, used: 0 });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchProxies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/proxies`);
      const processed = [];

      if (Array.isArray(data.ProxyDetails)) {
        for (const d of data.ProxyDetails) {
          if (d && d.proxy) {
            processed.push({
              id: generateSafeId(d.proxy),
              proxy: d.proxy,
              status: d.status || 'Disponible',
              used_by: d.used_by || '',
              rotate_url: d.rotate_url || '',
            });
          }
        }
      } else if (Array.isArray(data.ProxyList)) {
        for (const proxy of data.ProxyList) {
          const status = data.ProxyStatus && data.ProxyStatus[proxy];
          processed.push({
            id: generateSafeId(proxy),
            proxy,
            status: status || 'Disponible',
            used_by: extractUsedBy(status),
          });
        }
      }

      if (data.UsedProxies) {
        for (const [sessionName, proxyUrl] of Object.entries(data.UsedProxies)) {
          const idx = processed.findIndex((p) => p.proxy === proxyUrl);
          if (idx >= 0) {
            processed[idx].used_by = sessionName;
            processed[idx].status = `Utilisé par la session: ${sessionName}`;
          } else {
            processed.push({
              id: generateSafeId(proxyUrl),
              proxy: proxyUrl,
              status: `Utilisé par la session: ${sessionName}`,
              used_by: sessionName,
            });
          }
        }
      }

      processed.sort((a, b) => (a.used_by && !b.used_by ? -1 : !a.used_by && b.used_by ? 1 : 0));
      setProxies(processed);

      const used = processed.filter((p) => p.used_by).length;
      setStats({ total: processed.length, available: processed.length - used, used });
      setLoading(false);
    } catch (error) {
      notify(`Erreur de chargement: ${error.response?.data?.error || error.message}`, 'error');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProxies();
    const interval = setInterval(fetchProxies, 30000);
    return () => clearInterval(interval);
  }, [fetchProxies]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!newProxy.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/proxies`, { proxy: newProxy.trim(), rotateUrl: newRotate.trim() });
      setAddOpen(false);
      setNewProxy('');
      setNewRotate('');
      await fetchProxies();
      notify(newRotate.trim() ? 'Proxy 4G ajouté (IP tournante)' : 'Proxy ajouté');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Teste un lien de rotation et affiche le résultat brut de la requête.
  const testRotate = async (url) => {
    if (!url.trim()) return;
    setRotateTesting(true);
    setRotateTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/proxies/rotate-test`, { rotateUrl: url.trim() });
      setRotateTestResult(data);
    } catch (e) {
      setRotateTestResult({ success: false, error: e.response?.data?.error || e.message });
    } finally {
      setRotateTesting(false);
    }
  };

  const RotateTestResult = () => {
    if (!rotateTestResult) return null;
    const r = rotateTestResult;
    return (
      <Alert severity={r.success ? 'success' : 'error'} sx={{ mt: 1.5 }}>
        {r.error ? (
          <>Échec : {r.error}</>
        ) : (
          <>
            <b>HTTP {r.status}</b> — {r.success ? 'la rotation répond ✅' : 'réponse en erreur ⚠️'}
            {r.body ? (
              <Box component="pre" sx={{ mt: 0.75, mb: 0, p: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.3)', fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 120, overflow: 'auto' }}>
                {r.body}
              </Box>
            ) : null}
          </>
        )}
      </Alert>
    );
  };

  // Rotation manuelle de l'IP d'un proxy 4G.
  const rotateNow = async (proxy) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proxies/rotate`, { proxy });
      if (data.success) notify('Rotation d\'IP déclenchée ✅');
      else notify(`Rotation KO : ${data.error}`, 'error');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Enregistre / met à jour / retire le lien de rotation d'un proxy existant.
  const saveRotateLink = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/proxies/rotate-link`, { proxy: rotateEdit.proxy, rotateUrl: rotateEdit.url.trim() });
      setRotateEdit({ open: false, proxy: '', url: '' });
      await fetchProxies();
      notify(rotateEdit.url.trim() ? 'Lien de rotation enregistré' : 'Lien de rotation retiré');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkText.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proxies/bulk`, { text: bulkText });
      setBulkOpen(false);
      setBulkText('');
      await fetchProxies();
      notify(data.message || 'Import terminé');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selection.length === 0) return;
    const selectedRows = proxies.filter((p) => selection.includes(p.id));
    const selectedProxies = selectedRows.map((p) => p.proxy);
    const usedCount = selectedRows.filter((p) => p.used_by).length;
    if (usedCount > 0 && !window.confirm(`${usedCount} proxy(s) sélectionné(s) sont utilisés.\nLes supprimer va détacher ces comptes (repassés en direct), à réassigner ensuite. Continuer ?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proxies/bulk-delete`, { proxies: selectedProxies });
      setSelection([]);
      await fetchProxies();
      notify(data.message || 'Suppression effectuée');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteOne = async (proxy, isUsed, usedBy) => {
    if (isUsed && !window.confirm(`Ce proxy est utilisé par ${usedBy}.\nLe supprimer va détacher ce compte (repassé en direct), à réassigner ensuite. Continuer ?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/api/proxies/${encodeURIComponent(proxy)}`);
      await fetchProxies();
      notify(data.message || 'Proxy supprimé');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleAssignUnassigned = async () => {
    if (!window.confirm('Réassigner automatiquement un proxy libre à tous les comptes sans proxy (direct) ?')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proxies/assign-unassigned`);
      await fetchProxies();
      notify(data.message || 'Réassignation effectuée');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleReload = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proxies/reload`);
      await fetchProxies();
      notify(`${data.count} proxies rechargés`);
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  // réassigner une session unique en direct (bouton ligne)
  const reassignDirect = async (sessionName) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proxies/reassign`, {
        sessions: [sessionName],
        target: 'direct',
      });
      await fetchProxies();
      notify(data.message || 'Réassigné en direct');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const openReassign = async () => {
    setReassignOpen(true);
    setReassignSel([]);
    try {
      const { data } = await axios.get(`${API}/api/sessions`);
      const list = Array.isArray(data) ? data : data.sessions || [];
      setSessions(list);
    } catch (e) {
      notify(`Impossible de charger les sessions: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  const applyReassign = async () => {
    if (reassignSel.length === 0) return;
    const target = reassignTarget === 'custom' ? customProxy.trim() : reassignTarget;
    if (reassignTarget === 'custom' && !target) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proxies/reassign`, {
        sessions: reassignSel,
        target,
      });
      setReassignOpen(false);
      await fetchProxies();
      notify(data.message || 'Réassignation effectuée');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleReassign = (name) =>
    setReassignSel((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const sessionProxy = (s) => s.ProxyURL || s.proxy_url || 'direct';

  const testProxyIP = async (proxy) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proxies/check`, { proxy });
      if (data.ok) {
        notify(
          `IP de sortie : ${data.ip} (serveur : ${data.server_ip || '?'}) — ${data.via_proxy ? '✓ passe bien par le proxy' : '⚠️ identique à l\'IP serveur !'}`,
          data.via_proxy ? 'success' : 'warning'
        );
      } else {
        notify(`Proxy KO : ${data.error} (IP serveur : ${data.server_ip || '?'})`, 'error');
      }
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── Colonnes DataGrid ────────────────────────────────────────────────────────
  const columns = [
    {
      field: 'proxy',
      headerName: 'Proxy',
      flex: 1,
      minWidth: 280,
      renderCell: (params) => {
        const isUsed = Boolean(params.row.used_by);
        const is4G = Boolean(params.row.rotate_url);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <NetworkCheckIcon sx={{ fontSize: 15, color: isUsed ? t.warning : t.success }} />
            <Typography noWrap sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
              {params.row.proxy}
            </Typography>
            {is4G && (
              <Tooltip title={`Proxy 4G — IP tournante après chaque post. Rotate: ${params.row.rotate_url}`}>
                <Chip icon={<SignalIcon sx={{ fontSize: 12, color: '#c7d2fe !important' }} />} label="4G" size="small"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${t.primary}22`, color: '#c7d2fe', border: `1px solid ${t.primary}55` }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Statut',
      width: 130,
      renderCell: (params) => {
        const isUsed = Boolean(params.row.used_by);
        return (
          <Chip
            label={isUsed ? 'Utilisé' : 'Libre'}
            size="small"
            color={isUsed ? 'warning' : 'success'}
            variant="filled"
          />
        );
      },
    },
    {
      field: 'used_by',
      headerName: 'Session',
      width: 190,
      renderCell: (params) =>
        params.row.used_by ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: t.foreground }}>
            <WarningIcon sx={{ fontSize: 14, color: t.warning }} />
            <Typography sx={{ fontSize: '0.82rem' }}>{params.row.used_by}</Typography>
          </Box>
        ) : (
          <Typography sx={{ color: t.mutedFaint }}>—</Typography>
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 210,
      sortable: false,
      renderCell: (params) => {
        const isUsed = Boolean(params.row.used_by);
        const is4G = Boolean(params.row.rotate_url);
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Tester l'IP de sortie (prouve que le proxy marche)">
              <IconButton size="small" onClick={() => testProxyIP(params.row.proxy)} sx={{ color: t.info }}>
                <NetworkCheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {is4G && (
              <Tooltip title="Rotate maintenant (changer l'IP)">
                <IconButton size="small" onClick={() => rotateNow(params.row.proxy)} sx={{ color: t.primary }}>
                  <AutorenewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={is4G ? 'Modifier le lien de rotation 4G' : 'Définir un lien de rotation (proxy 4G)'}>
              <IconButton size="small" onClick={() => setRotateEdit({ open: true, proxy: params.row.proxy, url: params.row.rotate_url || '' })} sx={{ color: is4G ? t.primary : t.mutedFaint }}>
                <SignalIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isUsed && (
              <Tooltip title={`Passer ${params.row.used_by} en direct`}>
                <IconButton size="small" onClick={() => reassignDirect(params.row.used_by)} sx={{ color: t.info }}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isUsed ? `Supprimer (détache ${params.row.used_by}, à réassigner)` : 'Supprimer'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteOne(params.row.proxy, isUsed, params.row.used_by)}
                  sx={{ color: t.error }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Proxies
          </Typography>
          <Typography variant="body2" sx={{ color: t.muted, mt: 0.5 }}>
            Ajout, import/suppression en masse et réassignation — tout est persisté automatiquement.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<SwapHorizIcon />} onClick={openReassign}>
            Réassigner
          </Button>
          <Tooltip title="Réassigne un proxy libre à tous les comptes sans proxy (direct)">
            <Button variant="outlined" color="warning" startIcon={<BoltIcon />} onClick={handleAssignUnassigned} disabled={busy}>
              Auto-assigner
            </Button>
          </Tooltip>
          <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={handleReload} disabled={busy}>
            Recharger
          </Button>
          <Button variant="outlined" startIcon={<PlaylistAddIcon />} onClick={() => setBulkOpen(true)}>
            Import en masse
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Ajouter
          </Button>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={fetchProxies}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<NetworkCheckIcon />} label="Total proxies" value={stats.total} color={t.primary} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<CheckCircleIcon />} label="Disponibles" value={stats.available} color={t.success} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<WarningIcon />} label="Utilisés" value={stats.used} color={t.warning} />
        </Grid>
      </Grid>

      {/* Barre d'action sélection */}
      {selection.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: `${t.primary}14`, borderColor: `${t.primary}55` }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
              {selection.length} proxy(s) sélectionné(s)
            </Typography>
            <Button color="error" variant="contained" size="small" startIcon={<DeleteIcon />} onClick={handleBulkDelete} disabled={busy}>
              Supprimer la sélection
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tableau */}
      <Card>
        <Box sx={{ height: 620, width: '100%' }}>
          <DataGrid
            rows={proxies}
            columns={columns}
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={(model) => setSelection(model)}
            rowSelectionModel={selection}
            getRowId={(row) => row.id}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': { borderColor: t.border },
              '& .MuiDataGrid-cell': { borderColor: t.border },
              '& .MuiDataGrid-footerContainer': { borderColor: t.border },
              '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(255,255,255,0.025)' },
            }}
          />
        </Box>
      </Card>

      {/* ── Dialog : Ajouter ─────────────────────────────────────────────── */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un proxy</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Format : <code>http://user:pass@host:port</code>
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="URL du proxy"
            value={newProxy}
            onChange={(e) => setNewProxy(e.target.value)}
            placeholder="http://user:pass@host:port"
          />
          <Box sx={{ mt: 2.5, p: 1.75, borderRadius: 2, bgcolor: `${t.primary}0d`, border: `1px solid ${t.primary}33` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <SignalIcon sx={{ fontSize: 16, color: t.primary }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Proxy 4G (optionnel)</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: t.muted, mb: 1.5 }}>
              Un seul proxy HTTP partagé par plusieurs comptes. Après chaque publication via ce proxy,
              on appelle le lien de rotation pour changer d'IP avant de passer au compte suivant.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Lien de rotation d'IP"
              value={newRotate}
              onChange={(e) => setNewRotate(e.target.value)}
              placeholder="https://.../rotate?token=..."
            />
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 1.25 }}
              startIcon={rotateTesting ? <CircularProgress size={14} /> : <AutorenewIcon sx={{ fontSize: 16 }} />}
              onClick={() => testRotate(newRotate)}
              disabled={rotateTesting || !newRotate.trim()}
            >
              Tester la rotation
            </Button>
            <RotateTestResult />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAddOpen(false); setRotateTestResult(null); }} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleAdd} variant="contained" startIcon={<AddIcon />} disabled={busy}>
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog : Import en masse ─────────────────────────────────────── */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import en masse</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Collez un proxy par ligne. Les doublons et lignes invalides sont ignorés automatiquement.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={8}
            maxRows={18}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={'http://user:pass@host:port\nhttp://user:pass@host:port\n...'}
            InputProps={{ sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: '0.82rem' } }}
          />
          <Typography sx={{ mt: 1, fontSize: '0.78rem', color: t.muted }}>
            {bulkText.split('\n').filter((l) => l.trim()).length} ligne(s) détectée(s)
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleBulkImport} variant="contained" startIcon={<PlaylistAddIcon />} disabled={busy}>
            Importer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog : Réassignation ───────────────────────────────────────── */}
      <Dialog open={reassignOpen} onClose={() => setReassignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Réassigner les proxies des sessions</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: t.muted, mb: 1.5, mt: 0.5 }}>
            Cible de la réassignation :
          </Typography>
          <ToggleButtonGroup
            value={reassignTarget}
            exclusive
            onChange={(e, v) => v && setReassignTarget(v)}
            size="small"
            sx={{ mb: 1, flexWrap: 'wrap' }}
          >
            <ToggleButton value="direct">
              <LinkOffIcon fontSize="small" sx={{ mr: 0.75 }} /> Direct (sans proxy)
            </ToggleButton>
            <ToggleButton value="auto">
              <BoltIcon fontSize="small" sx={{ mr: 0.75 }} /> Auto (proxy libre)
            </ToggleButton>
            <ToggleButton value="custom">
              <NetworkCheckIcon fontSize="small" sx={{ mr: 0.75 }} /> Proxy précis
            </ToggleButton>
          </ToggleButtonGroup>

          {reassignTarget === 'custom' && (
            <TextField
              fullWidth
              size="small"
              label="URL du proxy"
              value={customProxy}
              onChange={(e) => setCustomProxy(e.target.value)}
              placeholder="http://user:pass@host:port"
              sx={{ mb: 1 }}
            />
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.85rem', color: t.muted }}>
              Sessions ({reassignSel.length} sélectionnée(s))
            </Typography>
            <Button
              size="small"
              color="inherit"
              onClick={() =>
                setReassignSel(reassignSel.length === sessions.length ? [] : sessions.map((s) => s.Name || s.name))
              }
            >
              {reassignSel.length === sessions.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </Button>
          </Box>

          <List dense sx={{ maxHeight: 280, overflow: 'auto', border: `1px solid ${t.border}`, borderRadius: 2 }}>
            {sessions.length === 0 && (
              <ListItem>
                <ListItemText primary="Aucune session chargée…" primaryTypographyProps={{ color: t.muted }} />
              </ListItem>
            )}
            {sessions.map((s) => {
              const name = s.Name || s.name;
              const proxy = sessionProxy(s);
              const isDirect = proxy === 'direct' || proxy === '';
              return (
                <ListItem
                  key={name}
                  button
                  onClick={() => toggleReassign(name)}
                  sx={{ borderRadius: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 38 }}>
                    <Checkbox edge="start" size="small" checked={reassignSel.includes(name)} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={name}
                    secondary={isDirect ? 'direct' : proxy}
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                    secondaryTypographyProps={{
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: '0.7rem',
                      color: isDirect ? t.info : t.mutedFaint,
                      sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReassignOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={applyReassign}
            variant="contained"
            startIcon={<SwapHorizIcon />}
            disabled={busy || reassignSel.length === 0}
          >
            Réassigner ({reassignSel.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog : lien de rotation 4G ─────────────────────────────────── */}
      <Dialog open={rotateEdit.open} onClose={() => setRotateEdit({ open: false, proxy: '', url: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Lien de rotation 4G</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Proxy : <code>{rotateEdit.proxy}</code>. Laisse vide pour retirer le lien (proxy classique).
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Lien de rotation d'IP"
            value={rotateEdit.url}
            onChange={(e) => setRotateEdit((s) => ({ ...s, url: e.target.value }))}
            placeholder="https://.../rotate?token=..."
          />
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1.25 }}
            startIcon={rotateTesting ? <CircularProgress size={14} /> : <AutorenewIcon sx={{ fontSize: 16 }} />}
            onClick={() => testRotate(rotateEdit.url)}
            disabled={rotateTesting || !rotateEdit.url.trim()}
          >
            Tester la rotation
          </Button>
          <RotateTestResult />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setRotateEdit({ open: false, proxy: '', url: '' }); setRotateTestResult(null); }} color="inherit">Annuler</Button>
          <Button onClick={saveRotateLink} variant="contained" startIcon={<SignalIcon />} disabled={busy}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FixedProxies;
